import { useCallback, useEffect, useRef, useState } from 'react';
import ReferenceScreen from '../../components/reference/ReferenceScreen';
import RowStatus from '../../components/reference/RowStatus';
import FormAlert from '../../components/reference/FormAlert';
import { referenceApi } from '../../services/referenceApi';
import { apiErrorMessage, blankToNull, isNotFound } from '../../services/apiErrors';
import type { Ingredient, Unit } from '../../services/referenceApi';

type Editing = { kind: 'new' } | { kind: 'edit'; row: Ingredient } | null;

function byName(a: Ingredient, b: Ingredient) {
  return a.name.localeCompare(b.name);
}

const EMPTY = { name: '', itemCode: '', itemName: '', unitId: '', poundsPerBushel: '' };

/** What the buyer buys, with its own item codes, each counted in one unit. */
export default function IngredientsPage({ slug }: { slug: string }) {
  const [rows, setRows] = useState<Ingredient[] | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loadError, setLoadError] = useState('');
  const [buyerMissing, setBuyerMissing] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);
  const [form, setForm] = useState(EMPTY);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [switchError, setSwitchError] = useState('');
  const nameRef = useRef<HTMLInputElement>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setLoadError('');
    setBuyerMissing(false);
    setEditing(null);
    setSwitchError('');
    setSwitching(null);
    Promise.all([referenceApi.listIngredients(slug), referenceApi.listUnits(slug)])
      .then(([ingredients, unitList]) => {
        if (cancelled) return;
        setRows([...ingredients.data].sort(byName));
        setUnits(unitList.data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (isNotFound(err)) setBuyerMissing(true);
        else setLoadError('We could not load the ingredients just now. Check your connection and try again.');
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const unitName = (id: string) => units.find((u) => u.id === id)?.name ?? '';

  useEffect(() => {
    if (editing) nameRef.current?.focus();
  }, [editing]);

  const open = useCallback((next: Editing) => {
    setEditing(next);
    setSaveError('');
    setForm(
      next?.kind === 'edit'
        ? {
            name: next.row.name,
            itemCode: next.row.item_code ?? '',
            itemName: next.row.item_name ?? '',
            unitId: next.row.unit_id,
            poundsPerBushel: next.row.pounds_per_bushel ? String(Number(next.row.pounds_per_bushel)) : '',
          }
        : EMPTY
    );
  }, []);

  const set = (field: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const place = (saved: Ingredient) =>
    setRows((current) => [...(current ?? []).filter((r) => r.id !== saved.id), saved].sort(byName));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setSaveError('');
    setSaving(true);
    const body = {
      name: form.name.trim(),
      item_code: blankToNull(form.itemCode),
      item_name: blankToNull(form.itemName),
      unit_id: form.unitId,
      pounds_per_bushel: blankToNull(form.poundsPerBushel),
    };
    try {
      const { data } =
        editing.kind === 'new'
          ? await referenceApi.addIngredient(slug, body)
          : await referenceApi.editIngredient(slug, editing.row.id, body);
      place(data);
      setEditing(null);
      addButtonRef.current?.focus();
    } catch (err) {
      setSaveError(apiErrorMessage(err, 'We could not save the ingredient just now. Check your connection and try again.'));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (row: Ingredient) => {
    setSwitchError('');
    setSwitching(row.id);
    try {
      const { data } = await referenceApi.editIngredient(slug, row.id, { active: !row.active });
      place(data);
    } catch (err) {
      setSwitchError(apiErrorMessage(err, `We could not switch ${row.name} ${row.active ? 'off' : 'on'} just now.`));
    } finally {
      setSwitching(null);
    }
  };

  // The units a row may use: the ones switched on, plus the one it already has.
  const unitOptions = units.filter((u) => u.active || u.id === form.unitId);

  return (
    <ReferenceScreen
      slug={slug}
      title="Ingredients"
      lead={(buyer) => `What ${buyer} buys, with the item codes its own system uses. Each ingredient is counted in one unit.`}
      addLabel="Add ingredient"
      onAdd={() => open({ kind: 'new' })}
      addButtonRef={addButtonRef}
      buyerMissing={buyerMissing}
    >
      {editing && (
        <form onSubmit={handleSubmit} className="mt-8 space-y-5 rounded-md border border-line bg-surface p-6" noValidate>
          <h2 className="text-heading text-ink">{editing.kind === 'new' ? 'New ingredient' : `Edit ${editing.row.name}`}</h2>
          <FormAlert lead="Ingredient not saved." message={saveError} />
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="ing-name" className="label">
                Name
              </label>
              <input id="ing-name" ref={nameRef} type="text" required maxLength={100} value={form.name} onChange={set('name')} className="input" />
            </div>
            <div>
              <label htmlFor="ing-unit" className="label">
                Unit
              </label>
              <select id="ing-unit" required value={form.unitId} onChange={set('unitId')} className="input">
                <option value="">Choose a unit</option>
                {unitOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="ing-item-code" className="label">
                Item code
              </label>
              <input
                id="ing-item-code"
                type="text"
                maxLength={50}
                aria-describedby="ing-item-code-hint"
                value={form.itemCode}
                onChange={set('itemCode')}
                className="input tabular-nums"
              />
              <p id="ing-item-code-hint" className="mt-1 text-caption text-ink-muted">
                The buyer&apos;s own code for this ingredient, as its system lists it.
              </p>
            </div>
            <div>
              <label htmlFor="ing-item-name" className="label">
                Item name
              </label>
              <input id="ing-item-name" type="text" maxLength={200} value={form.itemName} onChange={set('itemName')} className="input" />
            </div>
            <div>
              <label htmlFor="ing-ppb" className="label">
                Pounds per bushel
              </label>
              <input
                id="ing-ppb"
                type="text"
                inputMode="decimal"
                aria-describedby="ing-ppb-hint"
                value={form.poundsPerBushel}
                onChange={set('poundsPerBushel')}
                className="input tabular-nums"
              />
              <p id="ing-ppb-hint" className="mt-1 text-caption text-ink-muted">
                Only for grains quoted in bushels, for example 56 for corn.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" className="btn-primary" disabled={saving || !form.name.trim() || !form.unitId}>
              {saving ? 'Saving' : editing.kind === 'new' ? 'Save ingredient' : 'Save changes'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="mt-8">
        {switchError && <FormAlert lead="Not switched." message={switchError} />}
        {loadError && <FormAlert lead="Ingredients not loaded." message={loadError} />}
        {rows === null && !loadError && (
          <p className="text-body text-ink-muted" role="status">
            Loading ingredients
          </p>
        )}
        {rows !== null && rows.length === 0 && <p className="text-body text-ink-muted">No ingredients yet.</p>}
        {rows !== null && rows.length > 0 && (
          // The table keeps its columns and scrolls inside its own box on a narrow screen. The box is
          // relative so the hidden Actions label stays inside it and cannot widen the page.
          <div className="relative overflow-x-auto">
          <table className="w-full border-collapse text-body">
            <caption className="sr-only">Ingredients</caption>
            <thead>
              <tr className="border-b border-line-strong text-left text-caption font-semibold text-ink-muted">
                <th scope="col" className="py-2 pr-4">
                  Name
                </th>
                <th scope="col" className="py-2 pr-4">
                  Item code
                </th>
                <th scope="col" className="py-2 pr-4">
                  Item name
                </th>
                <th scope="col" className="py-2 pr-4">
                  Unit
                </th>
                <th scope="col" className="py-2 pr-4 text-right">
                  Lb per bushel
                </th>
                <th scope="col" className="py-2 pr-4">
                  Status
                </th>
                <th scope="col" className="py-2 text-right">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className={`border-b border-line ${row.active ? 'text-ink' : 'text-ink-muted'}`}>
                  <td className="py-3 pr-4 font-semibold">{row.name}</td>
                  <td className="py-3 pr-4 tabular-nums">{row.item_code ?? ''}</td>
                  <td className="py-3 pr-4">{row.item_name ?? ''}</td>
                  <td className="py-3 pr-4">{unitName(row.unit_id)}</td>
                  <td className="py-3 pr-4 text-right tabular-nums">{row.pounds_per_bushel ? String(Number(row.pounds_per_bushel)) : ''}</td>
                  <td className="py-3 pr-4">
                    <RowStatus active={row.active} />
                  </td>
                  <td className="py-3 text-right">
                    <div className="flex justify-end gap-3">
                      <button type="button" className="text-caption font-semibold text-navy underline-offset-4 hover:underline" onClick={() => open({ kind: 'edit', row })}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="text-caption font-semibold text-navy underline-offset-4 hover:underline disabled:opacity-60"
                        disabled={switching === row.id}
                        onClick={() => toggle(row)}
                      >
                        {row.active ? 'Switch off' : 'Switch on'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </ReferenceScreen>
  );
}
